import React, { useState } from 'react';
import { Loader2, KeyRound, Lock } from 'lucide-react';
import { authAPI } from '../services/api';
import { toast } from 'sonner';

export default function UserSecurityModal({ open, onClose, user }) {
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingPin, setSavingPin] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  if (!open) return null;

  const resetAndClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setNewPin('');
    setConfirmPin('');
    onClose?.();
  };

  const handleChangePassword = async () => {
    if (!String(currentPassword || '').trim()) {
      toast.error('Indica a password atual');
      return;
    }

    if (String(newPassword || '').length < 6) {
      toast.error('A nova password deve ter pelo menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('A confirmação da nova password não coincide');
      return;
    }

    try {
      setSavingPassword(true);
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Password alterada com sucesso');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao alterar password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleChangePin = async () => {
    const normalized = String(newPin || '').replace(/\D+/g, '');
    const normalized2 = String(confirmPin || '').replace(/\D+/g, '');

    if (normalized.length < 4 || normalized.length > 6) {
      toast.error('O PIN POS deve ter 4 a 6 dígitos');
      return;
    }

    if (normalized !== normalized2) {
      toast.error('A confirmação do PIN não coincide');
      return;
    }

    if (!user?.id) {
      toast.error('Utilizador inválido');
      return;
    }

    try {
      setSavingPin(true);
      await authAPI.setPosPin(user.id, normalized);
      toast.success('PIN POS atualizado com sucesso');
      setNewPin('');
      setConfirmPin('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao atualizar PIN POS');
    } finally {
      setSavingPin(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center px-4"
      style={{ background: 'rgba(2, 6, 23, 0.72)' }}
      onClick={resetAndClose}
    >
      <div
        className="w-full max-w-2xl rounded-3xl overflow-hidden"
        style={{ background: '#f8fafc', border: '1px solid #cbd5e1' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b" style={{ borderColor: '#e2e8f0' }}>
          <p className="text-2xl font-bold text-slate-900">Definições pessoais</p>
          <p className="text-sm text-slate-600 mt-1">
            {user?.name || 'Utilizador'} · Segurança de acesso
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div
            className="rounded-2xl p-5"
            style={{ background: '#ffffff', border: '1px solid #cbd5e1' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-slate-700" />
              <p className="text-lg font-semibold text-slate-900">Alterar password</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password atual
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full rounded-2xl px-4 py-3 text-sm outline-none"
                  style={{ background: '#fff', border: '1px solid #cbd5e1', color: '#0f172a' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nova password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full rounded-2xl px-4 py-3 text-sm outline-none"
                  style={{ background: '#fff', border: '1px solid #cbd5e1', color: '#0f172a' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Confirmar nova password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-2xl px-4 py-3 text-sm outline-none"
                  style={{ background: '#fff', border: '1px solid #cbd5e1', color: '#0f172a' }}
                />
              </div>

              <button
                onClick={handleChangePassword}
                disabled={savingPassword}
                className="w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}
              >
                {savingPassword ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    A guardar...
                  </span>
                ) : (
                  'Guardar password'
                )}
              </button>
            </div>
          </div>

          <div
            className="rounded-2xl p-5"
            style={{ background: '#ffffff', border: '1px solid #cbd5e1' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <KeyRound className="w-5 h-5 text-emerald-700" />
              <p className="text-lg font-semibold text-slate-900">Alterar PIN POS</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Novo PIN
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={newPin}
                  onChange={(e) => setNewPin(String(e.target.value || '').replace(/\D+/g, '').slice(0, 6))}
                  className="w-full rounded-2xl px-4 py-3 text-sm outline-none"
                  style={{ background: '#fff', border: '1px solid #cbd5e1', color: '#0f172a' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Confirmar PIN
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(String(e.target.value || '').replace(/\D+/g, '').slice(0, 6))}
                  className="w-full rounded-2xl px-4 py-3 text-sm outline-none"
                  style={{ background: '#fff', border: '1px solid #cbd5e1', color: '#0f172a' }}
                />
              </div>

              <button
                onClick={handleChangePin}
                disabled={savingPin}
                className="w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}
              >
                {savingPin ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    A guardar...
                  </span>
                ) : (
                  'Guardar PIN POS'
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end" style={{ borderColor: '#e2e8f0' }}>
          <button
            onClick={resetAndClose}
            className="rounded-2xl px-5 py-2.5 text-sm font-semibold"
            style={{ background: '#fff', border: '1px solid #cbd5e1', color: '#334155' }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
